import useOnMount from '@/hooks/useOnMount';
import { Button } from '@cmpsr/components';
import { useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { PaymentFormState } from '../PPRAPaymentPage';
import { StickyPurchaseButton } from './StickyPurchaseButton';
import { VARS } from '../../common/constants';

interface PurchaseSectionProps {
  form: UseFormReturn<PaymentFormState>;
  isPaymentComplete: boolean;
  currentPrice: number | null;
  setShowPaymentError: (value: boolean) => void;
  paymentSectionRef: React.RefObject<HTMLDivElement>;
  handleSubmit: () => void;
  isSubmitting: boolean;
}

const PurchaseSection = ({
  form,
  isPaymentComplete,
  setShowPaymentError,
  paymentSectionRef,
  handleSubmit,
  isSubmitting,
}: PurchaseSectionProps) => {
  const mainButtonRef = useRef<HTMLButtonElement>(null);
  const [showStickyButton, setShowStickyButton] = useState(false);

  const handlePurchase = () => {
    const callback = async () => {
      await form.trigger();

      // If payment is not complete, show payment error
      if (!isPaymentComplete) {
        setShowPaymentError(true);
      }

      // If form is not valid or payment is not complete, scroll to payment section and trigger validation
      if (!form.formState.isValid || !isPaymentComplete) {
        form.trigger(); // Trigger form validation to show errors
        paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Otherwise process the purchase using the controller's handleSubmit
      handleSubmit();
    };

    callback();
  };

  // Effect to handle the sticky button
  useOnMount(() => {
    const checkMainButtonVisibility = () => {
      if (mainButtonRef.current) {
        const mainButtonPosition = mainButtonRef.current.getBoundingClientRect();
        const isScrolledPast = mainButtonPosition.bottom < 0;
        setShowStickyButton(isScrolledPast);
      }
    };

    window.addEventListener('scroll', checkMainButtonVisibility);
    window.addEventListener('resize', checkMainButtonVisibility);

    // Initial check
    checkMainButtonVisibility();

    return () => {
      window.removeEventListener('scroll', checkMainButtonVisibility);
      window.removeEventListener('resize', checkMainButtonVisibility);
    };
  });

  return (
    <>
      <Button
        ref={mainButtonRef}
        width="100%"
        py={5}
        backgroundColor="green"
        textColor="darkBlue"
        onClick={handlePurchase}
        isLoading={isSubmitting}
      >
        {VARS.cta.message}
      </Button>
      {showStickyButton && <StickyPurchaseButton onClick={handlePurchase} isLoading={isSubmitting} />}
    </>
  );
};

export default PurchaseSection;
